import { useState } from 'react';
import { Button } from '@appwin/ui';
import { api, ApiError, type AddVersionInput } from './api.js';

/** Adds a published version that points at the publisher's installer. */
export function AddVersionForm({
  appId,
  onClose,
  onAdded,
}: {
  appId: string;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [version, setVersion] = useState('');
  const [downloadUrl, setDownloadUrl] = useState('');
  const [sha256, setSha256] = useState('');
  const [sizeMb, setSizeMb] = useState('');
  const [kind, setKind] = useState<AddVersionInput['kind']>('exe');
  const [arch, setArch] = useState<AddVersionInput['arch'][number]>('x64');
  const [scope, setScope] = useState<AddVersionInput['scope']>('per_user');
  const [silent, setSilent] = useState('/S');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needsSilent = kind === 'exe' || kind === 'msi';

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const mb = sizeMb.trim() ? Math.round(Number(sizeMb.replace(',', '.')) * 1024 * 1024) : undefined;
      await api.addVersion(appId, {
        version: version.trim(),
        platform: 'windows',
        arch: [arch],
        kind,
        scope,
        downloadUrl: downloadUrl.trim(),
        sha256: sha256.trim().toLowerCase(),
        ...(mb && Number.isFinite(mb) && mb > 0 ? { sizeBytes: mb } : {}),
        ...(needsSilent ? { silentArgs: silent.split(/\s+/).filter(Boolean) } : {}),
      });
      onAdded();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Не удалось добавить версию');
    } finally {
      setBusy(false);
    }
  };

  const valid =
    version.trim().length > 0 &&
    /^https:\/\/.+/i.test(downloadUrl.trim()) &&
    /^[a-f0-9]{64}$/i.test(sha256.trim()) &&
    (!needsSilent || silent.trim().length > 0);

  return (
    <div className="fixed inset-0 z-10 flex items-center justify-center bg-black/60 p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-border bg-surface p-6">
        <h2 className="text-lg font-semibold">Добавить версию</h2>
        <p className="mt-1 text-xs text-muted">{appId}</p>
        <p className="mt-3 rounded-lg border border-border bg-bg p-3 text-xs text-muted">
          Версия публикуется сразу. Установщик остаётся на сервере издателя, клиент проверяет
          SHA-256 перед установкой.
        </p>

        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Версия">
              <input
                value={version}
                onChange={(event) => setVersion(event.target.value)}
                placeholder="1.0.0"
                disabled={busy}
                className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm"
              />
            </Field>
            <Field label="Тип пакета">
              <select
                value={kind}
                onChange={(event) => setKind(event.target.value as AddVersionInput['kind'])}
                disabled={busy}
                className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm"
              >
                <option value="exe">EXE</option>
                <option value="msi">MSI</option>
                <option value="msix">MSIX</option>
                <option value="appx">APPX</option>
                <option value="zip">ZIP (портативная)</option>
              </select>
            </Field>
          </div>

          <Field label="Ссылка на установщик" hint="Только https, прямая ссылка на файл.">
            <input
              value={downloadUrl}
              onChange={(event) => setDownloadUrl(event.target.value)}
              placeholder="https://vendor.example/app.exe"
              disabled={busy}
              className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm"
            />
          </Field>

          <Field label="SHA-256 файла" hint="64 шестнадцатеричных символа. Обязательно.">
            <input
              value={sha256}
              onChange={(event) => setSha256(event.target.value)}
              placeholder="a1b2c3…"
              disabled={busy}
              className="h-10 w-full rounded-lg border border-border bg-bg px-3 font-mono text-sm"
            />
          </Field>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Размер, МБ">
              <input
                value={sizeMb}
                onChange={(event) => setSizeMb(event.target.value)}
                placeholder="12.5"
                disabled={busy}
                className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm"
              />
            </Field>
            <Field label="Архитектура">
              <select
                value={arch}
                onChange={(event) => setArch(event.target.value as AddVersionInput['arch'][number])}
                disabled={busy}
                className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm"
              >
                <option value="x64">x64</option>
                <option value="x86">x86</option>
                <option value="arm64">ARM64</option>
                <option value="universal">универсальная</option>
              </select>
            </Field>
            <Field label="Установка">
              <select
                value={scope}
                onChange={(event) => setScope(event.target.value as AddVersionInput['scope'])}
                disabled={busy}
                className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm"
              >
                <option value="per_user">для пользователя</option>
                <option value="machine">для всех</option>
                <option value="portable">портативная</option>
              </select>
            </Field>
          </div>

          {needsSilent ? (
            <Field label="Параметры тихой установки" hint="Нужны для автообновления.">
              <input
                value={silent}
                onChange={(event) => setSilent(event.target.value)}
                placeholder="/S"
                disabled={busy}
                className="h-10 w-full rounded-lg border border-border bg-bg px-3 font-mono text-sm"
              />
            </Field>
          ) : null}
        </div>

        {error ? <p className="mt-3 text-sm text-danger">{error}</p> : null}

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={busy || !valid}>
            {busy ? 'Сохранение…' : 'Опубликовать'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted">{label}</span>
      {children}
      {hint ? <span className="mt-1 block text-xs text-muted">{hint}</span> : null}
    </label>
  );
}
